import { useEffect } from "react"

/** Registers a ``beforeunload`` listener while ``when`` is truthy.
 *
 *  Browsers ignore custom messages — what shows is the platform's
 *  standard "leave site?" dialog. Use this sparingly: every false-
 *  positive prompt trains users to mash through the dialog and
 *  defeats the safety net for cases that actually matter.
 *
 *  Good fits in this app: site-notes textarea has unsaved edits,
 *  issue-report assistant has pending attachments staged but not yet
 *  sent (so the upload would orphan if the page unloaded). Bad fits:
 *  generic "user typed something somewhere" — the per-turn backend
 *  persistence already covers most of that. */
export function useBeforeUnloadGuard(when: boolean): void {
  useEffect(() => {
    if (!when) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Required for older browsers (Chrome <119) — modern engines
      // ignore the string entirely and show their own copy.
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [when])
}
